import React, { useState } from 'react';
import { Button } from '@kcd/design-system';

import { useOAIInvalidateQueries } from '~/hooks/reactQuery';
import { OAIApiError } from '~/libs/openApiError';
import { logError } from '~/utils/datadog';
import { appAlert, openDialogAsync } from '~/utils/mobileApp';

import type { ProductInquireFormValue } from './ProductInquireBottomSheet';
import ProductInquireBottomSheet from './ProductInquireBottomSheet';
import type { ProductInquiry } from './ProductInquireContainer';
import ProductInquireContainer from './ProductInquireContainer';
import {
  useCreateProductInquire,
  useDeleteProductInquire,
  useEditProductInquire,
} from '../queries';

type Props = {
  offerId?: number;
};
function ProductInquire({ offerId }: Props) {
  const [opened, setOpened] = useState(false);
  const [editingInquire, setEditingInquire] = useState<
    ProductInquiry | undefined
  >(undefined);
  const { mutateAsync: createProductInquire } = useCreateProductInquire();

  const { mutateAsync: editProductInquire } = useEditProductInquire();
  const { mutateAsync: deleteProductInquire } = useDeleteProductInquire();

  const invalidateQueries = useOAIInvalidateQueries();

  const onOpenInquireEdit = (values: ProductInquiry) => {
    setOpened(true);
    setEditingInquire(values);
  };

  const onInquireEdit = async (values: ProductInquireFormValue) => {
    try {
      if (!editingInquire) throw new Error('수정할 수 없는 상품문의 입니다.');

      await editProductInquire({
        inquiryId: editingInquire.id!,
        requestBody: { offerId: editingInquire.offerId!, ...values },
      });
      await invalidateQueries(
        '/api/market-place/v1/product-inquiries/{offerId}',
      );

      setOpened(false);
      appAlert({
        description: '상품문의 수정이 완료되었습니다.',
      });
    } catch (err) {
      setOpened(false);
      appAlert({
        description:
          '상품문의 작성이 실패했습니다. 자세한 내용은 고객센터로 문의 후 다시 시도해주세요.',
      });

      logError(err as Error);
    }
  };

  const onInquireCreate = async (values: ProductInquireFormValue) => {
    try {
      await createProductInquire({
        requestBody: {
          offerId: offerId!,
          ...values,
        },
      });
      await invalidateQueries(
        '/api/market-place/v1/product-inquiries/{offerId}',
      );

      setOpened(false);
      appAlert({
        description: '상품문의 등록이 완료되었습니다.',
      });
    } catch (err) {
      setOpened(false);
      appAlert({
        description:
          '상품문의 작성이 실패했습니다. 자세한 내용은 고객센터로 문의 후 다시 시도해주세요.',
      });

      if (err instanceof OAIApiError) {
        logError(err);
      }
    }
  };

  const onInquireDelete = async (inquiryId: number) => {
    const { confirmed } = await openDialogAsync({
      type: 'destructive',
      description: '작성한 상품문의를 삭제하시겠습니까?',
    });

    if (confirmed) {
      await deleteProductInquire({ inquiryId });
    }
    await invalidateQueries('/api/market-place/v1/product-inquiries/{offerId}');
  };

  return (
    <div className="pt-16">
      <div className="mb-24 px-20">
        <Button
          variant="outline"
          type="button"
          block
          onClick={() => setOpened(true)}
        >
          상품문의 작성하기
        </Button>
      </div>

      <React.Suspense fallback={null}>
        <ProductInquireContainer
          offerId={offerId!}
          onEdit={onOpenInquireEdit}
          onDelete={onInquireDelete}
        />
      </React.Suspense>
      <ProductInquireBottomSheet
        opened={opened}
        onClose={() => {
          setEditingInquire(undefined);
          setOpened(false);
        }}
        onSubmit={editingInquire ? onInquireEdit : onInquireCreate}
        defaultValues={editingInquire}
      />
    </div>
  );
}

export default ProductInquire;
